import { useEffect, useState } from 'react';
import { supabase, TABLES } from '@/lib/supabase';

/**
 * The GamerPlug team (gamerplug_team_members), for the GP badge next to admin
 * names. It's a handful of rows, so it's fetched once per page load and shared
 * by every badge. Matched by user id where the caller has one, else by
 * gamertag (case-insensitive), since many name sites only carry the tag.
 *
 * The table is readable by signed-in users only, on purpose: logged-out
 * visitors skip the lookup and see no badges.
 */
export type GpTeam = { ids: Set<string>; gamertags: Set<string> };

const EMPTY: GpTeam = { ids: new Set(), gamertags: new Set() };

// A failed or logged-out load is retried after this long, or as soon as a
// session appears, rather than kept for the whole page session.
const RETRY_AFTER_MS = 60_000;

let cached: GpTeam | null = null;
let failedAt = 0;
let inflight: Promise<GpTeam> | null = null;
const listeners = new Set<(team: GpTeam) => void>();

async function fetchGpTeam(): Promise<GpTeam | null> {
  const { data: session } = await supabase.auth.getSession();
  if (!session.session) return null;

  const { data: rows, error } = await supabase
    .from(TABLES.GAMERPLUG_TEAM_MEMBERS)
    .select('user_id');
  if (error || !rows?.length) return null;

  const ids = rows.map((row) => row.user_id as string);
  const { data: users, error: usersError } = await supabase
    .from(TABLES.USERS)
    .select('gamertag')
    .in('id', ids);
  if (usersError) return null;

  return {
    ids: new Set(ids),
    gamertags: new Set(
      (users ?? [])
        .map((user) => (user.gamertag as string | null)?.toLowerCase())
        .filter((tag): tag is string => !!tag),
    ),
  };
}

export function getCachedGpTeam(): GpTeam | null {
  return cached;
}

export function loadGpTeam(): Promise<GpTeam> {
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;
  if (failedAt && Date.now() - failedAt < RETRY_AFTER_MS) return Promise.resolve(EMPTY);

  inflight = fetchGpTeam()
    .catch(() => null)
    .then((team) => {
      inflight = null;
      if (!team) {
        failedAt = Date.now();
        return EMPTY;
      }
      cached = team;
      failedAt = 0;
      listeners.forEach((listener) => listener(team));
      return team;
    });
  return inflight;
}

/** Called with the team whenever it loads or is cleared (sign-out). */
export function subscribeGpTeam(listener: (team: GpTeam) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// Logging in client-side doesn't remount badges, so load as soon as a session
// appears; clear on sign-out so the next viewer starts fresh.
if (typeof window !== 'undefined') {
  supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_OUT') {
      cached = null;
      failedAt = 0;
      listeners.forEach((listener) => listener(EMPTY));
      return;
    }
    if (session && !cached) {
      failedAt = 0;
      void loadGpTeam();
    }
  });
}

export function isGpTeam(team: GpTeam, userId?: string | null, gamertag?: string | null): boolean {
  if (userId && team.ids.has(userId)) return true;
  if (gamertag && team.gamertags.has(gamertag.toLowerCase())) return true;
  return false;
}

/**
 * Is this user on the GamerPlug team? null while the list is loading. Used to
 * keep team-only features (the web feed during its V1) out of everyone else's
 * way. UI gating only: anything such a page shows must already be readable by
 * the signed-in user.
 */
export function useIsGpTeamMember(userId?: string | null): boolean | null {
  const [team, setTeam] = useState<GpTeam | null>(() => getCachedGpTeam());
  useEffect(() => {
    if (!userId) return;
    let alive = true;
    loadGpTeam().then((t) => alive && setTeam(t));
    const unsubscribe = subscribeGpTeam((t) => alive && setTeam(t));
    return () => {
      alive = false;
      unsubscribe();
    };
  }, [userId]);
  if (!userId) return false;
  if (!team) return null;
  return team.ids.has(userId);
}
